import { createApi } from "@reduxjs/toolkit/query/react";
import config from "../../config/config";
import axiosBaseQuery from "./axiosBaseQuery/axiosBaseQuery";

// Types for API response and payload
export interface LeaderBusinessBackgroundAudit {
  createdDate?: string;
  createdBy?: string;
  updatedDate?: string;
  updatedBy?: string;
}

export type SuccessorType = 1 | 2 | 3; // 1=yes, 2=no, 3=in progress

export interface LeaderBusinessBackgroundBody {
  businessDetail?: string;
  successorType?: SuccessorType;
  successorDetail?: string;
  completionRate?: number;
  audit?: LeaderBusinessBackgroundAudit;
}

export interface APIHeadAuthentication {
  loginSessionStatus: number;
  userSessionStatus: number;
  systemAvailability: number;
}

export interface APIHead {
  authentication: APIHeadAuthentication;
}

export interface APIErrorItem {
  errorCode: number;
  errorMessage: string;
}

export interface LeaderBusinessBackgroundAPIResponse {
  statusCode: number;
  head: APIHead;
  body: LeaderBusinessBackgroundBody;
  error: APIErrorItem[];
}

export interface LeaderBusinessBackgroundPayload {
  businessDetail: string;
  successorType: SuccessorType;
  successorDetail: string;
}

export const leaderBusinessBackgroundAPI = createApi({
  reducerPath: "leaderBusinessBackgroundAPI",
  baseQuery: axiosBaseQuery({
    baseUrl: config?.appUrl,
  }),
  keepUnusedDataFor: 900,
  tagTypes: ["LeaderBusinessBackground"],
  endpoints: (builder) => ({
    getLeaderBusinessBackground: builder.query<LeaderBusinessBackgroundAPIResponse, UserSpecificArgTypes>({
      query: ({ affiliateCode, aboNumber }) => ({
        url: `/am/abo/${affiliateCode}/${aboNumber}/profile/business/background`,
        method: "GET",
      }),
      providesTags: (_result, _error, { affiliateCode, aboNumber }) => [
        { type: "LeaderBusinessBackground", id: `${affiliateCode}-${aboNumber}` },
      ],
      transformResponse: (response: LeaderBusinessBackgroundAPIResponse) => response || ({} as LeaderBusinessBackgroundAPIResponse),
    }),
    saveLeaderBusinessBackground: builder.mutation<
      LeaderBusinessBackgroundAPIResponse,
      { affiliateCode: string; aboNumber: number; payload: LeaderBusinessBackgroundPayload }
    >({
      query: ({ affiliateCode, aboNumber, payload }) => ({
        url: `/am/abo/${affiliateCode}/${aboNumber}/profile/business/background`,
        method: "POST",
        data: payload,
      }),
      async onQueryStarted({ affiliateCode, aboNumber }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled; // data: LeaderBusinessBackgroundAPIResponse
          dispatch(
            leaderBusinessBackgroundAPI.util.updateQueryData(
              "getLeaderBusinessBackground",
              { affiliateCode, aboNumber },
              (draft) => {
                if (!draft || !data) return;
                draft.statusCode = data.statusCode;
                draft.head = data.head;
                draft.body = data.body;
                draft.error = data.error;
              }
            )
          );
        } catch(err) {
          console.error(
            `[LeaderBusinessBackground] POST failed ${affiliateCode}-${aboNumber}`,
            (err as any)?.error ?? err
          );
        }
      },
    }),
  }),
});

export const { useGetLeaderBusinessBackgroundQuery, useSaveLeaderBusinessBackgroundMutation } = leaderBusinessBackgroundAPI;
